import { ToggleGroup } from 'radix-ui';
import { forwardRef, useId } from 'react';

export interface SegmentedOption {
  value: string;
  label: string;
}

type Tone = 'navy' | 'success' | 'danger';

const TONE_CLASSES: Record<Tone, string> = {
  navy: 'bg-navy text-white',
  success: 'bg-green text-white',
  danger: 'bg-danger text-white',
};

/**
 * `brand` (default) = member-facing forms (Instrument, Market Model) - brand
 * palette, Figma-locked, unaffected by the admin `data-theme` flip.
 * `admin` = admin-console toolbars (MYN-135: ExportDialog's format picker) -
 * discrete bordered pills on the semantic admin tokens (`accent`,
 * `row-selected`, `border-strong`, `text-primary`/`text-secondary`) so the
 * control repaints under `[data-theme='dark']` like every other admin
 * control. The two variants exist because member screens must render
 * byte-identical to their Figma-locked brand colors; a single shared token
 * set cannot serve both without changing one of them.
 */
type Variant = 'brand' | 'admin';

const CONTAINER_CLASSES: Record<Variant, string> = {
  brand: 'flex overflow-hidden rounded-button border border-input-border',
  admin: 'flex gap-2',
};

// Disabled treatment is shared across both variants: `disabled:pointer-events-none`
// suppresses hover entirely (rather than listing every hover class again under a
// disabled: prefix), and `disabled:opacity-50` dims whichever token colors are
// already applied instead of introducing a separate disabled color token.
const ITEM_BASE_CLASSES: Record<Variant, string> = {
  brand:
    'flex-1 font-sans text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dkgreen disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50',
  admin:
    'cursor-pointer rounded-lg border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50',
};

/**
 * `sm` tightens the brand segments' padding so long labels ("Only Invests In",
 * "Would rather not say") fit a narrow edit sidebar. The admin variant sizes
 * itself and ignores it.
 */
export type SegmentedSize = 'md' | 'sm';

const BRAND_SIZE_CLASSES: Record<SegmentedSize, string> = {
  md: 'px-4 py-3',
  sm: 'px-2 py-2.5',
};

const BRAND_DIVIDER_CLASSES = 'border-l border-input-border first:border-l-0';

const ADMIN_SELECTED_CLASSES = 'border-accent bg-row-selected text-text-primary active:bg-row-selected';
const ADMIN_UNSELECTED_CLASSES =
  'border-border-strong text-text-secondary hover:bg-row-hover hover:text-text-primary active:bg-row-selected active:text-text-primary';

/** Resolves the color classes for one segment - computed once per option, not re-derived per prop. */
function getToneClasses(
  variant: Variant,
  isSelected: boolean,
  optionValue: string,
  toneByValue?: Record<string, Tone>,
): string {
  if (variant === 'admin') {
    return isSelected ? ADMIN_SELECTED_CLASSES : ADMIN_UNSELECTED_CLASSES;
  }
  return isSelected ? TONE_CLASSES[toneByValue?.[optionValue] ?? 'navy'] : 'text-navy';
}

export interface SegmentedControlProps {
  label: string;
  options: readonly SegmentedOption[];
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  className?: string;
  /**
   * Colors the selected segment by its own value instead of always navy
   * (e.g. Market Model Preferences: Only=navy, Sometimes=green, Never=red).
   * Options not present in the map fall back to navy when selected. Only
   * meaningful on the `brand` variant - `admin` always uses the semantic
   * accent/row-selected pair.
   */
  toneByValue?: Record<string, Tone>;
  /**
   * `brand` (default) = existing member-form look, unchanged.
   * `admin` = discrete bordered pills on the admin semantic tokens.
   */
  variant?: Variant;
  /** Brand variant only: `sm` for long option labels in narrow panels. Default `md`. */
  size?: SegmentedSize;
  /** Brand variant only: a hairline between segments, so unselected options read as separate choices. */
  divided?: boolean;
  /** Disables every segment (e.g. while a dependent request is pending). */
  disabled?: boolean;
}

/**
 * Equal-width (brand) or discrete-pill (admin) segmented toggle, built on
 * Radix ToggleGroup. `type="single"` gives native radio semantics for free -
 * Radix renders `role="radiogroup"`/`role="radio"` and handles roving focus,
 * so this needed no bespoke ARIA wiring despite being a "complex interactive
 * widget" in the sense the standard reserves for Radix.
 */
export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps>(
  function SegmentedControl(
    {
      label,
      options,
      value,
      onChange,
      error,
      className = '',
      toneByValue,
      variant = 'brand',
      size = 'md',
      divided = false,
      disabled = false,
    },
    ref,
  ) {
    const errorId = useId();

    return (
      <div className="flex flex-col gap-1">
        <ToggleGroup.Root
          ref={ref}
          type="single"
          value={value ?? ''}
          onValueChange={(next) => {
            if (next) onChange(next); // ignore deselect - one segment stays active
          }}
          disabled={disabled}
          aria-label={label}
          aria-describedby={error ? errorId : undefined}
          className={`${CONTAINER_CLASSES[variant]} ${className}`.trim()}
        >
          {options.map((option) => {
            const isSelected = value === option.value;
            const toneClass = getToneClasses(variant, isSelected, option.value, toneByValue);
            return (
              <ToggleGroup.Item
                key={option.value}
                value={option.value}
                className={`${ITEM_BASE_CLASSES[variant]} ${variant === 'brand' ? BRAND_SIZE_CLASSES[size] : ''} ${variant === 'brand' && divided ? BRAND_DIVIDER_CLASSES : ''} ${toneClass}`}
              >
                {option.label}
              </ToggleGroup.Item>
            );
          })}
        </ToggleGroup.Root>
        {error && (
          <p id={errorId} role="alert" className="pl-1 text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    );
  },
);
